import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { AddressFields } from '@/components/admin/address-fields';
import { OpenRow } from '@/components/open-row';
import { Pager } from '@/components/pager';
import { usePagedList } from '@/lib/use-paged-list';
import { formatDate } from '@/components/status-timeline';
import { Badge, Callout, EmptyState, Field, buttonClass, inputClass, textareaClass } from '@/components/ui';
import { blankAddress, isBlankAddress, toPostalAddress, type AddressLike, type PostalAddress } from '@/lib/address';
import { formatCents } from '@/lib/money';
import { adminCustomerQuery, adminCustomersQuery } from '@/queries';

/**
 * Customers. The register, and the one place a bulk account is set up.
 *
 * 6,590 rows, every one of them imported from Shopify as `b2c`. Until this
 * screen there was no route to any of them: `customers` was a permission
 * module with policies on the table and nothing behind it, so the permission
 * decided who could reach nothing at all.
 *
 * WHAT THIS IS FOR, beyond looking somebody up. Spec 004 raises a Xero invoice
 * against a customer when a bulk order is approved. That needs a customer who
 * is marked `b2b`, with a payment term, usually with a credit limit. All three
 * are columns that were null on every row and settable by nobody. R-246.
 *
 * SPEC 016. A business customer is a company: an invoice email, a company
 * address, a delivery address, an ABN and the people who work there. A shop
 * buyer is one person, so a retail customer shows no contacts (FR-1608).
 */

interface CustomerRow {
  id: string;
  name: string;
  invoice_email: string | null;
  phone: string | null;
  kind: string;
  active: boolean;
  discount_bp: number | null;
  credit_limit_cents: number | null;
  payment_terms_days: number | null;
  balance_cents: number | null;
  overdue_cents: number | null;
  balance_at: string | null;
  approved_at: string | null;
  created_at: string;
  orders: number;
  cents: number;
  last: string | null;
}

const FILTERS: Array<{ key: string; label: string }> = [
  { key: 'all', label: 'Everyone' },
  { key: 'b2b', label: 'On account' },
  { key: 'b2c', label: 'Retail' },
];

interface Contact {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  position: string | null;
  is_main: boolean;
  active: boolean;
}

/** A JSON write to the API. The message the server gave, when it refuses. */
async function sendJson(method: string, url: string, body: unknown): Promise<void> {
  const res = await fetch(url, {
    method,
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  const parsed = (await res.json().catch(() => null)) as
    | { error?: { message?: string } } | null;
  if (!res.ok) throw new Error(parsed?.error?.message ?? 'Could not save it.');
}

/** Basis points as somebody reads them. 1000 is ten percent. */
function discountLabel(bp: number | null): string {
  if (!bp) return '';
  return `${(bp / 100).toFixed(bp % 100 === 0 ? 0 : 1)}%`;
}

/* -------------------------------------------------------------------------- */
/* One customer, opened                                                       */
/* -------------------------------------------------------------------------- */

type Company = CustomerRow & {
  notes: string | null;
  abn: string | null;
  company_address: AddressLike | null;
  delivery_address: AddressLike | null;
};

/**
 * Who the customer is and where things go. Spec 016 FR-1601, FR-1602.
 *
 * The delivery address starts as "Same as company address", which is what the
 * database holds as no delivery address. Clearing the tick shows a second set
 * of the same fields.
 */
function CompanyDetails({ customer, onSaved }: { customer: Company; onSaved: () => void }) {
  const fresh = () => ({
    name: customer.name,
    invoice_email: customer.invoice_email ?? '',
    phone: customer.phone ?? '',
    abn: customer.abn ?? '',
    company: customer.company_address ? toPostalAddress(customer.company_address) : blankAddress(),
    same: !customer.delivery_address,
    delivery: customer.delivery_address ? toPostalAddress(customer.delivery_address) : blankAddress(),
  });
  const [d, setD] = useState(fresh);
  const [said, setSaid] = useState<string | null>(null);
  // Read again when the saved record changes, not on every render.
  const saved = JSON.stringify([customer.id, customer.name, customer.invoice_email, customer.phone,
    customer.abn, customer.company_address, customer.delivery_address]);
  useEffect(() => { setD(fresh()); }, [saved]);

  const retail = customer.kind !== 'b2b';

  const save = useMutation({
    mutationFn: () => sendJson('PATCH', `/api/customers/${customer.id}`, {
      name: d.name,
      invoice_email: d.invoice_email,
      phone: d.phone,
      ...(!retail || customer.abn ? { abn: d.abn } : {}),
      company_address: isBlankAddress(d.company) ? null : d.company,
      delivery_address: d.same || isBlankAddress(d.delivery) ? null : d.delivery,
    }),
    onSuccess: () => { setSaid('Saved.'); onSaved(); },
    onError: (e: Error) => setSaid(e.message),
  });

  const setAddress = (k: 'company' | 'delivery') => (next: PostalAddress) =>
    setD((x) => ({ ...x, [k]: next }));

  return (
    <form
      className="mt-4"
      aria-label="Company details"
      onSubmit={(e) => { e.preventDefault(); setSaid(null); save.mutate(); }}
    >
      <h3 className="text-[13px] font-semibold">{retail ? 'Details' : 'Company'}</h3>
      <div className="mt-2 grid items-end gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Name" htmlFor="c-name" hint={retail ? undefined : 'The business or the club'}>
          <input id="c-name" className={inputClass} required value={d.name}
            onChange={(e) => setD((x) => ({ ...x, name: e.target.value }))} />
        </Field>
        <Field label="Invoice email" htmlFor="c-email" hint="The invoice and Xero use this address">
          <input id="c-email" type="email" className={inputClass} value={d.invoice_email}
            onChange={(e) => setD((x) => ({ ...x, invoice_email: e.target.value }))} />
        </Field>
        <Field label="Phone" htmlFor="c-phone" optional>
          <input id="c-phone" className={inputClass} value={d.phone}
            onChange={(e) => setD((x) => ({ ...x, phone: e.target.value }))} />
        </Field>
        {(!retail || customer.abn) && (
          <Field label="ABN" htmlFor="c-abn" optional>
            <input id="c-abn" className={inputClass} inputMode="numeric" value={d.abn}
              placeholder="51 824 753 556"
              onChange={(e) => setD((x) => ({ ...x, abn: e.target.value }))} />
          </Field>
        )}
      </div>

      <div className="mt-4 grid gap-5 lg:grid-cols-2">
        <fieldset>
          <legend className="text-[13px] font-semibold">{retail ? 'Address' : 'Company address'}</legend>
          <div className="mt-2">
            <AddressFields idPrefix="c-company" value={d.company} onChange={setAddress('company')} />
          </div>
        </fieldset>
        <fieldset>
          <legend className="text-[13px] font-semibold">Delivery address</legend>
          <label className="mt-2 flex items-center gap-2 text-[13px]">
            <input type="checkbox" checked={d.same}
              onChange={(e) => setD((x) => ({ ...x, same: e.target.checked }))} />
            {retail ? 'Same as the address' : 'Same as company address'}
          </label>
          {!d.same && (
            <div className="mt-3">
              <AddressFields idPrefix="c-delivery" value={d.delivery} onChange={setAddress('delivery')} />
            </div>
          )}
          <p className="mt-2 text-[12.5px] text-muted">
            A new order for this customer starts with this address. Staff can change it on the order.
          </p>
        </fieldset>
      </div>

      {said && <Callout tone={said === 'Saved.' ? 'ok' : 'danger'} title={said} className="mt-3" />}
      <div className="mt-3">
        <button type="submit" className={buttonClass('primary', 'sm')} disabled={save.isPending}>
          {save.isPending ? 'Saving' : retail ? 'Save the details' : 'Save the company'}
        </button>
      </div>
    </form>
  );
}

const NO_CONTACT = { name: '', email: '', phone: '', position: '' };

/** One contact's fields, for the add form and the edit row. */
function ContactFields({ idPrefix, value, onChange }: {
  idPrefix: string;
  value: typeof NO_CONTACT;
  onChange: (next: typeof NO_CONTACT) => void;
}) {
  const set = (k: keyof typeof NO_CONTACT) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...value, [k]: e.target.value });
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <Field label="Name" htmlFor={`${idPrefix}-name`}>
        <input id={`${idPrefix}-name`} className={inputClass} required value={value.name}
          onChange={set('name')} />
      </Field>
      <Field label="Position" htmlFor={`${idPrefix}-position`} optional>
        <input id={`${idPrefix}-position`} className={inputClass} value={value.position}
          placeholder="Uniform coordinator" onChange={set('position')} />
      </Field>
      <Field label="Email" htmlFor={`${idPrefix}-email`} optional>
        <input id={`${idPrefix}-email`} type="email" className={inputClass} value={value.email}
          onChange={set('email')} />
      </Field>
      <Field label="Phone" htmlFor={`${idPrefix}-phone`} optional>
        <input id={`${idPrefix}-phone`} className={inputClass} value={value.phone}
          onChange={set('phone')} />
      </Field>
    </div>
  );
}

/**
 * The people at the company. Spec 016 FR-1603, FR-1604.
 *
 * Remove switches a contact off. It stays in this list, faded, so the history
 * keeps who the business dealt with. The main contact receives the order and
 * proof emails (FR-1605); the invoice still goes to the invoice email.
 */
function Contacts({ customerId, contacts, onSaved }: {
  customerId: string;
  contacts: Contact[];
  onSaved: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(NO_CONTACT);
  const [editing, setEditing] = useState<string | null>(null);
  const [edit, setEdit] = useState(NO_CONTACT);
  const [said, setSaid] = useState<{ ok: boolean; text: string } | null>(null);

  const write = useMutation({
    mutationFn: (w: { method: string; url: string; body: unknown; done: string }) =>
      sendJson(w.method, w.url, w.body).then(() => w.done),
    onSuccess: (done: string) => {
      setSaid({ ok: true, text: done });
      setAdding(false);
      setEditing(null);
      setDraft(NO_CONTACT);
      onSaved();
    },
    onError: (e: Error) => setSaid({ ok: false, text: e.message }),
  });

  const patch = (c: Contact, body: unknown, done: string) => {
    setSaid(null);
    write.mutate({ url: `/api/customer-contacts/${c.id}`, method: 'PATCH', body, done });
  };

  return (
    <section className="mt-6" aria-label="Contacts">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-[13px] font-semibold">Contacts</h3>
        {!adding && (
          <button type="button" className={buttonClass('secondary', 'sm')}
            onClick={() => { setSaid(null); setAdding(true); }}>
            Add a contact
          </button>
        )}
      </div>
      <p className="mt-1 text-[12.5px] text-muted">
        Order and proof emails go to the main contact. With no main contact, they go to the
        invoice email. The invoice always goes to the invoice email.
      </p>

      {said && <Callout tone={said.ok ? 'ok' : 'danger'} title={said.text} className="mt-3" />}

      {adding && (
        <form
          className="mt-3 rounded-[8px] border border-line bg-raised p-3"
          aria-label="Add a contact"
          onSubmit={(e) => {
            e.preventDefault();
            setSaid(null);
            write.mutate({
              url: `/api/customers/${customerId}/contacts`, method: 'POST', body: draft,
              done: `Added ${draft.name.trim()}.`,
            });
          }}
        >
          <ContactFields idPrefix="cc-new" value={draft} onChange={setDraft} />
          <div className="mt-3 flex gap-2">
            <button type="submit" className={buttonClass('primary', 'sm')} disabled={write.isPending}>
              {write.isPending ? 'Adding' : 'Add the contact'}
            </button>
            <button type="button" className={buttonClass('secondary', 'sm')}
              onClick={() => { setAdding(false); setDraft(NO_CONTACT); }}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {contacts.length === 0
        ? <p className="mt-2 text-[13px] text-muted">No contacts. Add the people you deal with at this customer.</p>
        : (
          <ul className="mt-3 divide-y divide-line rounded-[8px] border border-line">
            {contacts.map((c) => (
              <li key={c.id} className={`p-3 ${c.active ? '' : 'opacity-60'}`}>
                {editing === c.id
                  ? (
                    <form
                      aria-label={`Edit ${c.name}`}
                      onSubmit={(e) => { e.preventDefault(); patch(c, edit, `Saved ${edit.name.trim()}.`); }}
                    >
                      <ContactFields idPrefix={`cc-${c.id}`} value={edit} onChange={setEdit} />
                      <div className="mt-3 flex gap-2">
                        <button type="submit" className={buttonClass('primary', 'sm')} disabled={write.isPending}>
                          Save the contact
                        </button>
                        <button type="button" className={buttonClass('secondary', 'sm')}
                          onClick={() => setEditing(null)}>
                          Cancel
                        </button>
                      </div>
                    </form>
                  )
                  : (
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="text-[13px]">
                        <p className="font-medium">
                          {c.name}
                          {c.position ? <span className="font-normal text-muted">, {c.position}</span> : null}
                          {c.is_main && <Badge tone="brand" className="ml-2">Main contact</Badge>}
                          {!c.active && <Badge className="ml-2">Removed</Badge>}
                        </p>
                        <p className="mt-0.5 text-muted">
                          {[c.email, c.phone].filter(Boolean).join(' · ') || 'No email or phone'}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {c.active && (
                          <button type="button" className={buttonClass('tertiary', 'sm')}
                            onClick={() => {
                              setSaid(null);
                              setEditing(c.id);
                              setEdit({
                                name: c.name, email: c.email ?? '', phone: c.phone ?? '',
                                position: c.position ?? '',
                              });
                            }}>
                            Edit
                          </button>
                        )}
                        {c.active && !c.is_main && (
                          <button type="button" className={buttonClass('tertiary', 'sm')}
                            disabled={write.isPending}
                            onClick={() => patch(c, { is_main: true }, `${c.name} is the main contact.`)}>
                            Make main contact
                          </button>
                        )}
                        <button type="button" className={buttonClass('secondary', 'sm')}
                          disabled={write.isPending}
                          onClick={() => patch(c, { active: !c.active }, c.active
                            ? `Removed ${c.name}. The contact stays in the history.`
                            : `${c.name} is back.`)}>
                          {c.active ? 'Remove' : 'Bring back'}
                        </button>
                      </div>
                    </div>
                  )}
              </li>
            ))}
          </ul>
        )}
    </section>
  );
}

/**
 * The terms panel.
 *
 * Kept next to the orders rather than on a separate screen, because the
 * question "should this customer be on thirty days" is answered by looking at
 * what they have bought, and two screens means nobody looks.
 */
function CustomerDetail({ customerId, onClose }: { customerId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery(adminCustomerQuery(customerId));
  const [said, setSaid] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string> | null>(null);

  const customer = data?.customer as Company | undefined;
  const contacts = (data?.contacts ?? []) as unknown as Contact[];
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'customer', customerId] });
    void qc.invalidateQueries({ queryKey: ['admin', 'customers'] });
  };

  useEffect(() => {
    if (!customer) return;
    setDraft({
      kind: customer.kind,
      payment_terms_days: customer.payment_terms_days?.toString() ?? '',
      credit_limit_cents: customer.credit_limit_cents != null
        ? (customer.credit_limit_cents / 100).toString() : '',
      discount_bp: customer.discount_bp?.toString() ?? '',
      notes: customer.notes ?? '',
    });
  }, [customer?.id, customer?.kind, customer?.payment_terms_days,
      customer?.credit_limit_cents, customer?.discount_bp, customer?.notes]);

  const save = useMutation({
    mutationFn: async (patch: Record<string, unknown>) => {
      const res = await fetch(`/api/customers/${customerId}`, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      if (!res.ok) throw new Error(body?.error?.message ?? 'Could not save it.');
    },
    onSuccess: () => {
      setSaid('Saved.');
      void qc.invalidateQueries({ queryKey: ['admin', 'customer', customerId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'customers'] });
    },
    onError: (e: Error) => setSaid(e.message),
  });

  if (isLoading) return <p className="p-4 text-[13px] text-muted">Reading the customer.</p>;
  if (error) return <Callout tone="danger" title={(error as Error).message} className="m-4" />;
  if (!customer || !draft) return null;

  const orders = (data?.orders ?? []) as Array<{
    id: string; ref: string; created_at: string; status: string; channel: string;
    total_cents: number; xero_invoice_number: string | null;
    paid_cents: number | null; due_cents: number | null;
  }>;

  return (
    <div className="mb-5 rounded-[10px] border border-line-strong bg-panel p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold tracking-[-0.02em]">{customer.name}</h2>
          <p className="mt-0.5 text-[13px] text-muted">
            {customer.invoice_email ?? 'no invoice email'}
            {customer.phone ? ` · ${customer.phone}` : ''}
            {` · ${data?.order_count ?? 0} order${data?.order_count === 1 ? '' : 's'}`}
          </p>
          {/* The credit decision, in the open. Goods leave before the money
              arrives, so the record has to say when somebody agreed to that. */}
          {customer.approved_at && (
            <p className="mt-1 text-[12px] text-muted">
              Approved for account on {formatDate(customer.approved_at)}
            </p>
          )}
        </div>
        <button type="button" className={buttonClass('tertiary', 'sm')} onClick={onClose}>
          Close
        </button>
      </div>

      {/* What Xero says they owe. Read back after an invoice is sent, so it is
          blank until this customer has been invoiced through the system. */}
      {customer.balance_cents != null && (
        <div className="mt-4 flex flex-wrap gap-6 rounded-[8px] border border-line bg-raised p-3">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-muted">Owing</p>
            <p className="font-[family-name:var(--font-mono)] text-[15px]">
              {formatCents(customer.balance_cents)}
            </p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-muted">Overdue</p>
            <p className={`font-[family-name:var(--font-mono)] text-[15px] ${
              Number(customer.overdue_cents ?? 0) > 0 ? 'text-[color:var(--color-danger-ink)]' : ''}`}
            >
              {formatCents(customer.overdue_cents ?? 0)}
            </p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-muted">As at</p>
            <p className="text-[13px]">{customer.balance_at ? formatDate(customer.balance_at) : 'never'}</p>
          </div>
        </div>
      )}

      <CompanyDetails customer={customer} onSaved={refresh} />

      {/* A shop buyer is one person (FR-1608). A retail customer that already
          has contacts, for example one made from a prospect, still shows them. */}
      {(customer.kind === 'b2b' || contacts.length > 0) && (
        <Contacts customerId={customerId} contacts={contacts} onSaved={refresh} />
      )}

      <h3 className="mt-6 text-[13px] font-semibold">Terms</h3>
      <form
        className="mt-2"
        onSubmit={(e) => {
          e.preventDefault();
          setSaid(null);
          save.mutate({
            kind: draft.kind,
            payment_terms_days: draft.payment_terms_days,
            // Entered in dollars, stored in cents. A person setting a credit
            // limit types 5000 and means five thousand dollars.
            credit_limit_cents: draft.credit_limit_cents === ''
              ? '' : Math.round(Number(draft.credit_limit_cents) * 100),
            discount_bp: draft.discount_bp,
            notes: draft.notes,
          });
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field
            label="Account"
            htmlFor="c-kind"
            hint="On account is a credit decision, recorded against you"
          >
            <select
              id="c-kind" className={inputClass} value={draft.kind}
              onChange={(e) => setDraft((d) => ({ ...d!, kind: e.target.value }))}
            >
              <option value="b2c">Retail, pays at the checkout</option>
              <option value="b2b">On account, invoiced</option>
            </select>
          </Field>
          <Field label="Payment terms" htmlFor="c-terms" hint="Days. 30 is usual">
            <input
              id="c-terms" className={inputClass} inputMode="numeric"
              value={draft.payment_terms_days} placeholder="30"
              onChange={(e) => setDraft((d) => ({ ...d!, payment_terms_days: e.target.value }))}
            />
          </Field>
          <Field label="Credit limit" htmlFor="c-limit" hint="Dollars. Empty means no limit">
            <input
              id="c-limit" className={inputClass} inputMode="decimal"
              value={draft.credit_limit_cents} placeholder="5000"
              onChange={(e) => setDraft((d) => ({ ...d!, credit_limit_cents: e.target.value }))}
            />
          </Field>
          <Field label="Discount" htmlFor="c-disc" hint="Basis points. 1000 is 10%">
            <input
              id="c-disc" className={inputClass} inputMode="numeric"
              value={draft.discount_bp} placeholder="0"
              onChange={(e) => setDraft((d) => ({ ...d!, discount_bp: e.target.value }))}
            />
          </Field>
        </div>

        <div className="mt-3">
        <Field label="Notes" htmlFor="c-notes" hint="What the next person needs to know">
          <textarea
            id="c-notes" rows={3} className={textareaClass} value={draft.notes}
            onChange={(e) => setDraft((d) => ({ ...d!, notes: e.target.value }))}
          />
        </Field>
        </div>

        {said && <Callout tone={said === 'Saved.' ? 'ok' : 'danger'} title={said} className="mt-3" />}

        <div className="mt-3 flex items-center gap-2">
          <button type="submit" className={buttonClass('primary', 'sm')} disabled={save.isPending}>
            {save.isPending ? 'Saving' : 'Save the terms'}
          </button>
          <button
            type="button" className={buttonClass('secondary', 'sm')} disabled={save.isPending}
            onClick={() => { setSaid(null); save.mutate({ active: !customer.active }); }}
          >
            {customer.active ? 'Switch off' : 'Switch back on'}
          </button>
        </div>
      </form>

      <h3 className="mt-6 text-[13px] font-semibold">Recent orders</h3>
      {orders.length === 0
        ? <p className="mt-1 text-[13px] text-muted">Nothing bought yet.</p>
        : (
          <div className="mt-2 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th scope="col">Reference</th>
                  <th scope="col">Date</th>
                  <th scope="col">Status</th>
                  <th scope="col" className="num">Total</th>
                  <th scope="col">Invoice</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id}>
                    <td className="font-[family-name:var(--font-mono)] text-[12.5px]">
                      <a href={`/orders/${o.ref}`} className="underline underline-offset-2">{o.ref}</a>
                    </td>
                    <td className="text-[13px] text-muted">{formatDate(o.created_at)}</td>
                    <td className="text-[13px]">{o.status.replace(/_/g, ' ')}</td>
                    <td className="num font-[family-name:var(--font-mono)] tabular-nums">
                      {formatCents(o.total_cents)}
                    </td>
                    <td className="text-[12.5px] text-muted">
                      {o.xero_invoice_number ?? ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Adding one                                                                 */
/* -------------------------------------------------------------------------- */

function AddCustomer({ onAdded }: { onAdded: () => void }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState({
    name: '', invoice_email: '', phone: '', kind: 'b2b', payment_terms_days: '30',
  });

  const add = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/customers', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(draft),
      });
      const body = (await res.json().catch(() => null)) as
        | { error?: { message?: string } } | null;
      if (!res.ok) throw new Error(body?.error?.message ?? 'Could not add the customer.');
    },
    onSuccess: () => {
      setDraft({ name: '', invoice_email: '', phone: '', kind: 'b2b', payment_terms_days: '30' });
      setError(null);
      setOpen(false);
      onAdded();
    },
    onError: (e: Error) => setError(e.message),
  });

  if (!open) {
    return (
      <button type="button" className={buttonClass('primary', 'sm')} onClick={() => setOpen(true)}>
        Add a customer
      </button>
    );
  }

  return (
    <form
      className="w-full rounded-[10px] border border-line bg-panel p-4"
      onSubmit={(e) => { e.preventDefault(); add.mutate(); }}
    >
      {/* Defaulted to on account, because a retail customer arrives through the
          shop and makes itself. Somebody typing one in here is setting up a
          club, a school or a business. */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Field label="Name" htmlFor="n-name" hint="The business or the club">
          <input
            id="n-name" className={inputClass} required value={draft.name}
            placeholder="Northside Netball Club"
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          />
        </Field>
        <Field label="Invoice email" htmlFor="n-email" hint="Where the invoice goes">
          <input
            id="n-email" type="email" className={inputClass} value={draft.invoice_email}
            placeholder="accounts@example.com.au"
            onChange={(e) => setDraft((d) => ({ ...d, invoice_email: e.target.value }))}
          />
        </Field>
        <Field label="Phone" htmlFor="n-phone" hint="Optional">
          <input
            id="n-phone" className={inputClass} value={draft.phone}
            onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))}
          />
        </Field>
        <Field label="Account" htmlFor="n-kind" hint="On account records your approval">
          <select
            id="n-kind" className={inputClass} value={draft.kind}
            onChange={(e) => setDraft((d) => ({ ...d, kind: e.target.value }))}
          >
            <option value="b2b">On account, invoiced</option>
            <option value="b2c">Retail, pays at the checkout</option>
          </select>
        </Field>
        <Field label="Payment terms" htmlFor="n-terms" hint="Days">
          <input
            id="n-terms" className={inputClass} inputMode="numeric"
            value={draft.payment_terms_days}
            onChange={(e) => setDraft((d) => ({ ...d, payment_terms_days: e.target.value }))}
          />
        </Field>
      </div>

      {error && <Callout tone="danger" title={error} className="mt-3" />}

      <div className="mt-3 flex items-center gap-2">
        <button type="submit" className={buttonClass('primary', 'sm')} disabled={add.isPending}>
          {add.isPending ? 'Adding' : 'Add the customer'}
        </button>
        <button
          type="button" className={buttonClass('secondary', 'sm')}
          onClick={() => { setOpen(false); setError(null); }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/* The register                                                               */
/* -------------------------------------------------------------------------- */

export default function AdminCustomersPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState('all');
  const [owing, setOwing] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  // The page number, the debounce and the reset to page one live in one place
  // now: they were the same twelve lines on six screens. R-272.
  const list = usePagedList<CustomerRow>(
    ({ page, q }) => adminCustomersQuery({ page, q, kind: filter, owing }) as never,
    [filter, owing],
  );
  const { page, setPage, query, setQuery, rows, isFetching, error } = list;
  // The two counts this screen shows beside its filters, which the database
  // counts over every row rather than over the page.
  const data = list.data as { b2b: number; owing: number } | undefined;

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Customers</h1>
          <p className="mt-1 text-[12.5px] text-muted">
            Everybody who has bought, and the terms of anybody who buys on account.
          </p>
        </div>
        <AddCustomer onAdded={() => void qc.invalidateQueries({ queryKey: ['admin', 'customers'] })} />
      </div>

      {/* A retail customer cannot be invoiced, so a system with no accounts is
          a system where the Xero bridge has nothing to raise an invoice for.
          Saying so is more useful than an empty filter. */}
      {data && data.b2b === 0 && (
        <Callout tone="warn" title="Nobody is set up on account yet" className="mb-4">
          Every customer came from Shopify and paid at the checkout. A bulk order can
          only be invoiced through Xero if its customer is marked on account, so set
          that here before the first bulk order is approved.
        </Callout>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={buttonClass(filter === f.key ? 'primary' : 'secondary', 'sm')}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
            {f.key === 'b2b' && data ? ` (${data.b2b})` : ''}
          </button>
        ))}
        <button
          type="button"
          className={buttonClass(owing ? 'primary' : 'secondary', 'sm')}
          onClick={() => setOwing((o) => !o)}
        >
          Overdue{data ? ` (${data.owing})` : ''}
        </button>
        <input
          className={`${inputClass} ml-auto w-[260px]`}
          placeholder="Name, email or phone"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search customers"
        />
      </div>

      {error && <Callout tone="danger" title={error.message} className="mb-4" />}

      {openId && <CustomerDetail customerId={openId} onClose={() => setOpenId(null)} />}

      <div className="rounded-[10px] border border-line bg-panel">
        {rows.length === 0 && !isFetching
          ? (
            <EmptyState
              title="No customer matches that"
              body="Search by name, email or phone. A retail customer is created by the
                    shop when they order, so most of these arrived with the Shopify
                    import."
            />
          )
          : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col">Invoice email</th>
                    <th scope="col">Account</th>
                    <th scope="col" className="num">Orders</th>
                    <th scope="col" className="num">Spent</th>
                    <th scope="col">Last order</th>
                    <th scope="col" className="num">Overdue</th>
                    <th scope="col"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <OpenRow
                      key={c.id}
                      className={c.active ? '' : 'opacity-60'}
                      label={`Open ${c.name}`}
                      onOpen={() => setOpenId(openId === c.id ? null : c.id)}
                    >
                      <td className="text-[13px] font-medium">{c.name}</td>
                      <td className="text-[12.5px] text-muted">{c.invoice_email ?? ''}</td>
                      <td>
                        {c.kind === 'b2b'
                          ? (
                            <Badge tone="brand">
                              On account
                              {c.payment_terms_days != null ? ` ${c.payment_terms_days}d` : ''}
                              {c.discount_bp ? ` · ${discountLabel(c.discount_bp)}` : ''}
                            </Badge>
                          )
                          : <span className="text-[12.5px] text-muted">Retail</span>}
                      </td>
                      <td className="num font-[family-name:var(--font-mono)] tabular-nums">{c.orders}</td>
                      <td className="num font-[family-name:var(--font-mono)] tabular-nums">
                        {formatCents(c.cents)}
                      </td>
                      <td className="text-[12.5px] text-muted">
                        {c.last ? formatDate(c.last) : ''}
                      </td>
                      <td className="num font-[family-name:var(--font-mono)] tabular-nums">
                        {Number(c.overdue_cents ?? 0) > 0
                          ? (
                            <span className="text-[color:var(--color-danger-ink)]">
                              {formatCents(c.overdue_cents!)}
                            </span>
                          )
                          : ''}
                      </td>
                      <td>
                        <button
                          type="button"
                          className={buttonClass('secondary', 'sm')}
                          onClick={() => setOpenId(openId === c.id ? null : c.id)}
                        >
                          {openId === c.id ? 'Close' : 'Open'}
                        </button>
                      </td>
                    </OpenRow>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        <Pager {...list.pager} noun="customer" onPage={setPage} />
      </div>
    </div>
  );
}
